// Pagina admin /encounters: lista paginata + filtri base.
// Sprint 3 minimal (analytics dashboard è Sprint 4).

import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { RuleBicolor } from '@audiq/ui';
import { api } from '../lib/api';
import { DateRangePicker, type DateRangeValue } from '../components/DateRangePicker';

type StatusFilter = 'all' | 'in_progress' | 'completed' | 'abandoned' | 'aborted_emergency';

const STATUS_LABELS: Record<Exclude<StatusFilter, 'all'>, string> = {
  in_progress: 'in corso',
  completed: 'completata',
  abandoned: 'abbandonata',
  aborted_emergency: 'emergenza',
};

const STATUS_COLORS: Record<Exclude<StatusFilter, 'all'>, string> = {
  in_progress: 'var(--accent)',
  completed: 'var(--primary)',
  abandoned: 'var(--mute)',
  aborted_emergency: 'var(--danger)',
};

export function EncountersListPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [dateRange, setDateRange] = useState<DateRangeValue>({ from: null, to: null });

  const query = useQuery({
    queryKey: ['encounters', { page, statusFilter, dateRange }],
    queryFn: () =>
      api.listEncounters({
        page,
        per_page: 20,
        status: statusFilter === 'all' ? undefined : statusFilter,
        date_from: dateRange.from ? dateRange.from.toISOString() : undefined,
        date_to: dateRange.to ? dateRange.to.toISOString() : undefined,
      }),
  });

  function fmt(ts?: string | null) {
    if (!ts) return '—';
    try {
      return new Date(ts).toLocaleString('it-IT', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    } catch {
      return ts;
    }
  }

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-2 mono" style={{ color: 'var(--mute)' }}>
        Conversazioni
      </div>
      <h2 className="serif text-3xl" style={{ color: 'var(--ink)' }}>
        Encounter recenti
      </h2>
      <p className="mt-2 text-sm" style={{ color: 'var(--mute)' }}>
        {query.data ? (
          <>
            Totale: <span className="mono">{query.data.total}</span> · pagina {page}
          </>
        ) : (
          'Carico…'
        )}
      </p>

      <RuleBicolor className="my-6" />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {(
          ['all', 'in_progress', 'completed', 'abandoned', 'aborted_emergency'] as StatusFilter[]
        ).map((s) => (
          <button
            key={s}
            onClick={() => {
              setStatusFilter(s);
              setPage(1);
            }}
            className="rounded-full border px-3 py-1 text-xs transition"
            style={{
              background: statusFilter === s ? 'var(--primary)' : 'var(--paper)',
              color: statusFilter === s ? '#fff' : 'var(--ink)',
              borderColor: 'var(--line)',
            }}
          >
            {s === 'all' ? 'Tutti' : STATUS_LABELS[s]}
          </button>
        ))}
        <span className="ms-2">
          <DateRangePicker
            value={dateRange}
            onChange={(r) => {
              setDateRange(r);
              setPage(1);
            }}
          />
        </span>
      </div>

      <div
        className="overflow-hidden rounded-md border"
        style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
      >
        <table className="w-full text-left text-sm">
          <thead className="mono text-xs" style={{ color: 'var(--mute)' }}>
            <tr style={{ borderBottom: '1px solid var(--line)' }}>
              <th className="px-3 py-2">Pseudonym</th>
              <th className="px-3 py-2">Iniziato</th>
              <th className="px-3 py-2">Stato</th>
              <th className="px-3 py-2">Lingua</th>
              <th className="px-3 py-2 text-end">Turni</th>
              <th className="px-3 py-2 text-end">Compl.</th>
              <th className="px-3 py-2 text-end">Flags</th>
              <th className="px-3 py-2 text-end">Esiti</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center" style={{ color: 'var(--mute)' }}>
                  Carico…
                </td>
              </tr>
            ) : query.data && query.data.encounters.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center" style={{ color: 'var(--mute)' }}>
                  Nessuna conversazione corrispondente ai filtri.
                </td>
              </tr>
            ) : (
              query.data?.encounters.map((e) => (
                <tr
                  key={e.id}
                  className="transition hover:bg-[var(--soft)]"
                  style={{ borderBottom: '1px solid var(--line)' }}
                >
                  <td className="px-3 py-2 mono">
                    <Link
                      to={`/encounters/${e.id}`}
                      className="underline"
                      style={{ color: 'var(--primary-2)' }}
                    >
                      {e.pseudonym}
                    </Link>
                  </td>
                  <td className="px-3 py-2 mono text-xs" style={{ color: 'var(--mute)' }}>
                    {fmt(e.started_at)}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className="mono inline-block rounded-full px-2 py-0.5 text-xs"
                      style={{
                        background: STATUS_COLORS[e.status],
                        color: e.status === 'abandoned' ? 'var(--ink)' : '#fff',
                      }}
                    >
                      {STATUS_LABELS[e.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 mono text-xs" style={{ color: 'var(--mute)' }}>
                    {e.language_detected ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-end mono">{e.total_turns}</td>
                  <td className="px-3 py-2 text-end mono">{e.completeness_pct}%</td>
                  <td className="px-3 py-2 text-end">
                    {e.flags_count > 0 ? (
                      <span
                        className="mono rounded-full px-2 py-0.5 text-xs"
                        style={{ background: 'var(--accent)', color: 'var(--ink)' }}
                      >
                        {e.flags_count}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--mute)' }}>0</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-end">
                    {e.outcomes_count > 0 ? (
                      <span
                        className="mono rounded-full px-2 py-0.5 text-xs"
                        style={{ background: 'var(--primary)', color: '#fff' }}
                      >
                        {e.outcomes_count}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--mute)' }}>0</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {query.data && query.data.total > query.data.per_page ? (
        <div className="mt-4 flex justify-between mono text-xs" style={{ color: 'var(--mute)' }}>
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded-md border px-3 py-1.5 disabled:opacity-50"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            Precedente
          </button>
          <span>
            Pagina {page} di {Math.ceil(query.data.total / query.data.per_page)}
          </span>
          <button
            disabled={page >= Math.ceil(query.data.total / query.data.per_page)}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-md border px-3 py-1.5 disabled:opacity-50"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            Successiva
          </button>
        </div>
      ) : null}
    </div>
  );
}
